import type { AxiosResponse } from 'axios';
import type { AssignedStaffLabel } from 'api/settings/assignedStaffLabelsService';
import type { Language } from 'js/vue/stores/languages/languagesStore';

export interface CustomTag {
    description: string;
    category: string;
    name: string;
}

export interface InvoicingSettingsMetadata {
    assigned_staff_labels: AssignedStaffLabel[];
    languages: Language[];
    custom_tags: CustomTag[];
    enforce_display_rules: boolean;
    forced_prefs: string[];
}

export interface FooterTranslation {
    lang_id: number;
    value: string;
}

export interface ManualFooter {
    mode: FooterMode.MANUAL;
    value: string;
    translations: FooterTranslation[];
}

interface AutoFooter {
    mode: FooterMode.AUTO;
}

export enum FooterMode {
    MANUAL = 'manual',
    AUTO = 'auto',
}

export interface DisplayPreferences {
    show_assigned_staff_identity: boolean;
    show_assigned_staff_email: boolean;
    show_assigned_staff_phone: boolean;
    show_assigned_staff_mobile: boolean;
    show_assigned_staff_fax: boolean;
    show_address: boolean;
    show_parent_document: boolean;
    show_contact_name: boolean;
    show_company_phone_number: boolean;
    show_vat_number: boolean;
    show_siret: boolean;
    show_siren: boolean;
    show_company_reference: boolean;
    show_reference_column: boolean;
    show_notes_column: boolean;
    show_unit_cost_column: boolean;
    show_taxes_column: boolean;
    show_quantity_column: boolean;
    show_amount_column: boolean;
    show_image_column: boolean;
    show_discount_column: boolean;
    show_barcode_values: boolean;
    show_barcode_images: boolean;
    show_payment_terms: boolean;
    show_payment_deadlines: boolean;
    show_payment_methods: boolean;
    show_deposit_details: boolean;
    show_bank_account: boolean;
    show_check_label: boolean;
    show_tax_basis: boolean;
    show_vat_mention: boolean;
    show_sale_type: boolean;
    show_payment_date: boolean;
    show_total: boolean;
    show_tax_details: boolean;
    show_item_count: boolean;
    show_item_weight: boolean;
    show_prices_on_delivery: boolean;
    show_signature_on_estimate: boolean;
    show_signature_on_order: boolean;
    show_signature_on_delivery: boolean;
    assigned_staff_label_id: number;
    footer: ManualFooter | AutoFooter;
    quantity_precision: number | null;
}

const baseUrl = '/invoicing/settings';

const getMetadata = (): Promise<AxiosResponse<InvoicingSettingsMetadata>> => {
    return ApiClient.get(`${baseUrl}/metadata`);
};

const getDisplayPreferences = (): Promise<AxiosResponse<DisplayPreferences>> => {
    return ApiClient.get(`${baseUrl}/display-preferences`);
};

const updateDisplayPreferences = (
    data: Partial<DisplayPreferences>,
): Promise<AxiosResponse<DisplayPreferences>> => {
    return ApiClient.patch(`${baseUrl}/display-preferences`, {}, data);
};

export { getMetadata, getDisplayPreferences, updateDisplayPreferences };
